import {
  classifyJobActivityStatus,
  formatSurfFeatureLabel,
  type SurfActivityJob,
  type SurfQueuedJob,
} from "@vida/surf-core";
import { useSurfEditorActivity } from "../../hooks/useSurfEditorActivity";
import { useSurfJobQueueOptional } from "../../hooks/useSurfJobQueue";

type Props = {
  jobs: SurfActivityJob[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
};

function statusClass(phase: ReturnType<typeof classifyJobActivityStatus>): string {
  if (phase === "running") return "surf-activity-status surf-activity-status--running";
  if (phase === "failed") return "surf-activity-status surf-activity-status--failed";
  return "surf-activity-status surf-activity-status--done";
}

function queueStatusClass(status: SurfQueuedJob["status"]): string {
  if (status === "pending" || status === "running") {
    return "surf-activity-status surf-activity-status--running";
  }
  if (status === "failed") return "surf-activity-status surf-activity-status--failed";
  return "surf-activity-status surf-activity-status--done";
}

function queueStatusLabel(status: SurfQueuedJob["status"]): string {
  if (status === "pending") return "IN CODA";
  if (status === "running") return "IN CORSO";
  if (status === "failed") return "FALLITO";
  if (status === "applied") return "APPLICATO";
  return "COMPLETATO";
}

function targetLabel(job: SurfQueuedJob): string {
  const parts: string[] = [];
  if (job.target.slideId) parts.push("slide");
  if (job.target.layerId) parts.push("widget");
  return parts.length ? parts.join(" · ") : job.deckId.slice(0, 8);
}

export function SurfActivityPanel({ jobs, loading, error, onRefresh }: Props) {
  const { message, progress } = useSurfEditorActivity();
  const queue = useSurfJobQueueOptional();
  const queueJobs = queue?.jobs ?? [];
  const running = jobs.filter((j) => classifyJobActivityStatus(j.status) === "running");
  const queueActive = queueJobs.filter(
    (j) => j.status === "pending" || j.status === "running",
  );

  return (
    <section className="surf-activity-panel" aria-label="Attività AI">
      <div className="surf-sidebar-section-head">
        <h2>Attività</h2>
        <button type="button" className="surf-icon-btn" onClick={() => onRefresh()} title="Aggiorna">
          ↻
        </button>
      </div>

      {(message || progress) && (
        <div className="surf-activity-live">
          {progress && (
            <p>
              Slide {progress.index + 1}/{progress.total} — {progress.status}
              {progress.message ? `: ${progress.message}` : ""}
            </p>
          )}
          {message && <p>{message}</p>}
        </div>
      )}

      {error && <p className="error surf-activity-error">{error}</p>}
      {loading && jobs.length === 0 && queueJobs.length === 0 && (
        <p className="muted">Caricamento job…</p>
      )}

      {!loading && jobs.length === 0 && queueJobs.length === 0 && !message && (
        <p className="muted">Nessun job AI recente.</p>
      )}

      {(running.length > 0 || queueActive.length > 0) && (
        <p className="muted surf-activity-running-count">
          {running.length + queueActive.length} in corso
        </p>
      )}

      {queueJobs.length > 0 && (
        <>
          <h3 className="surf-activity-subhead">Coda locale</h3>
          <ul className="surf-activity-list">
            {queueJobs.slice(0, 16).map((job) => (
              <li key={`q-${job.jobId}`} className="surf-activity-item">
                <div className="surf-activity-item-head">
                  <span className={queueStatusClass(job.status)}>{queueStatusLabel(job.status)}</span>
                  <span className="surf-activity-feature">{job.label}</span>
                </div>
                <p className="surf-activity-log muted">{targetLabel(job)}</p>
                {job.error && <p className="error surf-activity-log">{job.error}</p>}
                <div className="surf-activity-item-actions">
                  {job.status === "failed" && (
                    <button type="button" className="surf-link-btn" onClick={() => void queue?.retryJob(job.jobId)}>
                      Riprova
                    </button>
                  )}
                  {(job.status === "failed" || job.status === "applied") && (
                    <button type="button" className="surf-link-btn" onClick={() => void queue?.dismissJob(job.jobId)}>
                      Rimuovi
                    </button>
                  )}
                </div>
                <time className="muted surf-activity-time" dateTime={job.updatedAt}>
                  {new Date(job.updatedAt).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        </>
      )}

      {jobs.length > 0 && (
        <>
          <h3 className="surf-activity-subhead">Cloud</h3>
          <ul className="surf-activity-list">
            {jobs.slice(0, 12).map((job) => {
              const phase = classifyJobActivityStatus(job.status);
              const lastLog = job.clientLogLines[job.clientLogLines.length - 1];
              return (
                <li key={job.jobId} className="surf-activity-item">
                  <div className="surf-activity-item-head">
                    <span className={statusClass(phase)}>{job.status}</span>
                    <span className="surf-activity-feature">
                      {formatSurfFeatureLabel(job.feature)}
                    </span>
                  </div>
                  {job.prompt && (
                    <p className="surf-activity-prompt" title={job.prompt}>
                      {job.prompt.length > 72 ? `${job.prompt.slice(0, 72)}…` : job.prompt}
                    </p>
                  )}
                  {lastLog && <p className="surf-activity-log">{lastLog}</p>}
                  {job.errorMessage && <p className="error surf-activity-log">{job.errorMessage}</p>}
                  <time className="muted surf-activity-time" dateTime={job.updatedAt}>
                    {new Date(job.updatedAt).toLocaleString()}
                  </time>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
